import { Box, Stack, type SxProps, type Theme } from "@mui/material";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";

/**
 * Custom scrollbar wrapper that works consistently across all platforms, including iOS.
 *
 * Replaces native browser scrollbars with a custom implementation that provides:
 * - Always visible scrollbar (important for iOS where native scrollbars auto-hide)
 * - Draggable thumb for precise scrolling
 * - Clickable track for page up/down navigation
 * - Optional fade indicators to show scrollable content boundaries
 * - Fully customizable appearance
 */

interface ScrollbarWrapperProps {
  children: ReactNode;
  height: string;
  sx?: SxProps<Theme>;
  scrollbarWidth?: number;
  showScrollbarTrack?: boolean;
  showFadeIndicators?: boolean;
  fadeHeight?: string;
}

export function ScrollbarWrapper(props: ScrollbarWrapperProps) {
  const {
    children,
    height,
    sx,
    scrollbarWidth = 6,
    showScrollbarTrack = true,
    showFadeIndicators = true,
    fadeHeight = "40px",
  } = props;

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollbarRef = useRef<HTMLDivElement>(null);

  const [showTopFade, setShowTopFade] = useState(false);
  const [showBottomFade, setShowBottomFade] = useState(true);
  const [thumbHeight, setThumbHeight] = useState(0);
  const [thumbTop, setThumbTop] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ y: 0, scrollTop: 0 });

  // Calculate scrollbar thumb size, position, and fade indicator visibility
  const updateScrollbar = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = container;

    setShowTopFade(scrollTop > 10);
    setShowBottomFade(scrollTop + clientHeight < scrollHeight - 10);

    const thumbHeightRatio = clientHeight / scrollHeight;
    const calculatedThumbHeight = Math.max(thumbHeightRatio * clientHeight, 40);
    setThumbHeight(calculatedThumbHeight);

    const scrollRatio = scrollTop / (scrollHeight - clientHeight);
    const maxThumbTop = clientHeight - calculatedThumbHeight;
    setThumbTop(scrollRatio * maxThumbTop);
  }, []);

  const handleScroll = useCallback(() => {
    updateScrollbar();
  }, [updateScrollbar]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      updateScrollbar();
    }
  }, [children, updateScrollbar]);

  const handleThumbMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
    setIsDragging(true);
    dragStartRef.current = {
      y: event.clientY,
      scrollTop: scrollContainerRef.current?.scrollTop ?? 0,
    };
  };

  useEffect(() => {
    if (!isDragging) {
      return undefined;
    }

    function handleMouseMove(event: MouseEvent) {
      const container = scrollContainerRef.current;
      if (!container) {
        return;
      }

      const deltaY = event.clientY - dragStartRef.current.y;
      const { scrollHeight, clientHeight } = container;
      const maxThumbTop = clientHeight - thumbHeight;
      const scrollRange = scrollHeight - clientHeight;
      const deltaScroll = (deltaY / maxThumbTop) * scrollRange;

      container.scrollTop = dragStartRef.current.scrollTop + deltaScroll;
    }

    function handleMouseUp() {
      setIsDragging(false);
    }

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, thumbHeight]);

  const handleTrackClick = (event: React.MouseEvent) => {
    const container = scrollContainerRef.current;
    const track = scrollbarRef.current;
    if (!container || !track) {
      return;
    }

    const trackRect = track.getBoundingClientRect();
    const clickY = event.clientY - trackRect.top;

    if (clickY < thumbTop) {
      container.scrollTop -= container.clientHeight * 0.8;
    } else if (clickY > thumbTop + thumbHeight) {
      container.scrollTop += container.clientHeight * 0.8;
    }
  };

  return (
    <Box sx={{ position: "relative" }}>
      {/* Scrollable content */}
      <Stack
        ref={scrollContainerRef}
        sx={{
          height,
          overflowY: "scroll",
          pr: 1,
          WebkitOverflowScrolling: "touch",
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          ...sx,
        }}
        onScroll={handleScroll}
      >
        {children}
      </Stack>

      {/* Top fade indicator */}
      {showFadeIndicators && showTopFade && (
        <Box
          sx={(theme) => ({
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: fadeHeight,
            background: `linear-gradient(to bottom, ${theme.palette.background.primary} 0%, transparent 100%)`,
            pointerEvents: "none",
            zIndex: 2,
          })}
        />
      )}

      {/* Bottom fade indicator */}
      {showFadeIndicators && showBottomFade && (
        <Box
          sx={(theme) => ({
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: fadeHeight,
            background: `linear-gradient(to top, ${theme.palette.background.primary} 0%, transparent 100%)`,
            pointerEvents: "none",
            zIndex: 2,
          })}
        />
      )}

      {/* Scrollbar track */}
      <Box
        ref={scrollbarRef}
        sx={(theme) => ({
          position: "absolute",
          top: 0,
          right: 0,
          width: `${scrollbarWidth}px`,
          height,
          backgroundColor: showScrollbarTrack ? theme.palette.background.secondary : "transparent",
          borderRadius: `${scrollbarWidth / 2}px`,
          cursor: "pointer",
        })}
        onClick={handleTrackClick}
      >
        {/* Scrollbar thumb */}
        <Box
          sx={(theme) => ({
            position: "absolute",
            top: `${thumbTop}px`,
            left: 0,
            width: "100%",
            height: `${thumbHeight}px`,
            backgroundColor: theme.palette.border?.primary,
            borderRadius: `${scrollbarWidth / 2}px`,
            cursor: isDragging ? "grabbing" : "grab",
            transition: isDragging ? "none" : "background-color 0.2s",
            "&:hover": { backgroundColor: theme.palette.border?.dark },
          })}
          onMouseDown={handleThumbMouseDown}
        />
      </Box>
    </Box>
  );
}
